/* Pulls the SSN (and name, when legible) from OCR text of a Social Security
 * card. Ported from CDASS Enroll (src/extract/ssncard.js).
 */
import { titleCase, type IdFields } from "./aamva";

export function parseSsnCard(ocrText: string): IdFields | null {
  const out: Record<string, string> = {};
  const ssn = findSsn(ocrText);
  if (ssn) out.ssn = ssn;

  // The cardholder name is printed below the number. Take the first line that
  // looks like a name: 2-4 capitalized words, allowing a single-letter middle
  // initial (with optional period), no digits, and not boilerplate.
  const skip =
    /SOCIAL|SECURITY|ADMINISTRATION|UNITED|STATES|AMERICA|NUMBER|SIGNATURE|THIS|CARD|VALID|WORK|EMPLOYMENT|ONLY|PURPOSE/i;
  for (const line of ocrText.split(/\r?\n/)) {
    const t = line.trim().replace(/\s+/g, " ");
    if (!t || /\d/.test(t) || skip.test(t)) continue;
    if (/^[A-Z][A-Za-z'-]+(?:\s+(?:[A-Z]\.?|[A-Z][A-Za-z'-]+)){1,3}$/.test(t)) {
      const parts = t.replace(/\./g, "").split(/\s+/);
      out.first = titleCase(parts[0]);
      out.last = titleCase(parts[parts.length - 1]);
      if (parts.length > 2) out.middle = titleCase(parts.slice(1, -1).join(" "));
      break;
    }
  }
  return Object.keys(out).length ? (out as IdFields) : null;
}

// Strong OCR look-alikes for digits, applied only on the tolerant second pass.
const LOOKALIKE: Record<string, string> = {
  O: "0",
  o: "0",
  Q: "0",
  l: "1",
  I: "1",
  "|": "1",
  S: "5",
  B: "8",
  Z: "2",
};
const CH = "0-9OoQlI|SBZ";

function findSsn(text: string): string {
  // Real digits first (the common case): least chance of a false positive.
  for (const m of text.matchAll(/(?<![0-9])(\d{3})[\s.-]?(\d{2})[\s.-]?(\d{4})(?![0-9])/g)) {
    const d = `${m[1]}${m[2]}${m[3]}`;
    if (plausibleSsn(d)) return fmtSsn(d);
  }
  // Then tolerate common OCR letter-for-digit confusions (I->1, S->5, ...).
  const re = new RegExp(
    `(?<![${CH}])([${CH}]{3})[\\s.-]?([${CH}]{2})[\\s.-]?([${CH}]{4})(?![${CH}])`,
    "g",
  );
  for (const m of text.matchAll(re)) {
    const d = `${m[1]}${m[2]}${m[3]}`.replace(/[A-Za-z|]/g, (c) => LOOKALIKE[c] ?? c);
    if (/^\d{9}$/.test(d) && plausibleSsn(d)) return fmtSsn(d);
  }
  return "";
}

// Reject groupings the SSA never issues, so a stray number (a date, a phone
// number) is not mistaken for an SSN.
function plausibleSsn(d: string): boolean {
  const area = +d.slice(0, 3);
  const group = +d.slice(3, 5);
  const serial = +d.slice(5);
  return area !== 0 && area !== 666 && area < 900 && group !== 0 && serial !== 0;
}

function fmtSsn(d: string): string {
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}
